import { Icon } from '@design-system/index'
import { typo } from '@design-system/styles'
import styled from '@emotion/styled'
import React from 'react'

import { reporter } from '@/utils/modules/errorReporting/reporter'

import Button from './component/atoms/Button/Button/Button'
import { envUtils } from './utils/envUtils'

type Props = React.PropsWithChildren<{
  fallback: (reset: () => void) => React.ReactNode
}>

export class ErrorBoundary extends React.Component<Props, { hasError: boolean }> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  override componentDidMount() {
    // 브라우저 뒤로가기/앞으로가기 시 자동 리셋
    window.addEventListener('popstate', this.reset)
  }

  override componentWillUnmount() {
    window.removeEventListener('popstate', this.reset)
  }

  reset = () => {
    this.setState({ hasError: false })
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // console.error 로만 남기면 수집이 RUM 의 console 훅에 얹혀 가는 셈이라, 그 훅을 조정하는 순간
    // 크래시 관측이 통째로 사라진다. 보고는 명시적으로 한다.
    reporter.addBreadcrumb({
      category: 'custom',
      message: 'componentStack',
      data: { componentStack: errorInfo.componentStack ?? '' },
    })
    reporter.capture(error, { mechanism: 'boundary' })

    if (!envUtils.isLive) {
      console.error('App crashed:', error, errorInfo)
    }
  }

  override render() {
    if (this.state.hasError) {
      return this.props.fallback(this.reset)
    }
    return this.props.children
  }
}

export const ErrorBoundaryFallback = (_props: { reset: () => void }) => {
  return (
    <S.Container>
      <div className="notice-box">
        <div className="notice-header">
          <Icon name="alert_error_fill" size={32} color="var(--Colors-Gray-900)" />
          <h4>일시적 오류 발생 안내</h4>
        </div>
        <div className="notice-content">
          일시적인 오류가 발생하였습니다.
          <br />
          아래 &apos;홈으로 가기&apos; 버튼을 눌러 서비스에 다시 접근해 주세요.
        </div>
      </div>
      <Button
        variant="line-filled"
        size="l"
        color="neutral"
        width={120}
        onClick={() => {
          window.location.href = '/'
        }}
      >
        홈으로 가기
      </Button>
    </S.Container>
  )
}

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 48px;
    margin: auto;

    .notice-box {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 24px;
    }

    .notice-header {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;

      h4 {
        ${typo.heading04};
        font-weight: 700;
      }
    }

    .notice-content {
      text-align: center;
      color: var(--Colors-Gray-700);
    }
  `,
}
